import { Routes } from "#app/routes.ts";
import { useCreateConversationMutation } from "#features/conversations/api/useCreateConversationMutation.ts";
import { type CreateConversationMessageOptions, useCreateConversationMessage } from "#features/conversations/hooks/useCreateConversationMessage.ts";
import { useCreateConversationErrorHandler } from "#features/conversations/hooks/useCreateConversationErrorHandler.tsx";
import { useTracking } from "#hooks/useTracking.ts";
import { MixpanelEvent } from "#types/tracking.ts";
import { useCallback } from "react";
import { useNavigate } from "react-router";

export const useCreateConversation = () => {
    const { trackEvent } = useTracking();
    const navigate = useNavigate();

    const { mutate } = useCreateConversationMutation();
    const { createConversationMessage } = useCreateConversationMessage();
    const handleCreateConversationError = useCreateConversationErrorHandler();

    const createConversation = useCallback((content: string, options?: CreateConversationMessageOptions) => {
        options?.onMutate?.();

        // Reuse a conversation that was already created (e.g. eagerly at file-upload time).
        if (options?.existingConversationId) {
            const conversationId = options.existingConversationId;
            createConversationMessage(conversationId, content, options);
            navigate(Routes.Conversation(conversationId));

            return;
        }

        mutate(undefined, {
            onSuccess: ({ conversation }) => {
                const { conversationId } = conversation;
                trackEvent(MixpanelEvent.NewConversationStarted, {
                    "Conversation Id": conversationId
                });

                createConversationMessage(conversationId, content, options);

                navigate(Routes.Conversation(conversationId));
            },
            onError: error => {
                handleCreateConversationError(error);
                options?.onError?.();
            },
            onSettled: (_, error) => {
                // We call onSettled only if there was an error, because in the success case
                // onSettled is called in useCreateConversationMessageMutation's onSettled callback
                if (error) {
                    options?.onSettled?.();
                }
            }
        });
    }, [createConversationMessage, handleCreateConversationError, mutate, navigate, trackEvent]);

    return {
        createConversation
    };
};
